import { Alert, Button } from '@heroui/react';
import type React from 'react';
import { ApiErrorDetails } from '../apiErrorDetails';

type ErrorFallbackProps = {
    'data-testid': string;
    /**
     * What was caught. A development build shows an API error's technical
     * details under the message (`ApiErrorDetails`); production shows nothing
     * more.
     */
    error?: unknown;
    fillSpace?: boolean;
    id: string;
    message?: string;
    onRetry?: () => void;
    retryLabel?: string;
    title: string;
};

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
    'data-testid': dataTestId,
    error,
    fillSpace = false,
    id,
    message,
    onRetry,
    retryLabel,
    title,
}) => {
    return (
        <div
            className={
                fillSpace
                    ? 'flex h-full min-h-full w-full items-center justify-center'
                    : 'w-full'
            }
            data-testid={dataTestId}
            id={id}
        >
            <Alert
                className={
                    fillSpace
                        ? 'w-full border border-danger/20 bg-danger/5'
                        : 'w-full border border-danger/20 bg-danger/5'
                }
                data-testid={`${id}-alert`}
                id={`${id}-alert`}
                role="alert"
                status="danger"
            >
                <Alert.Indicator
                    data-testid={`${id}-indicator`}
                    id={`${id}-indicator`}
                />
                <Alert.Content
                    data-testid={`${id}-content`}
                    id={`${id}-content`}
                >
                    <Alert.Title data-testid={`${id}-title`} id={`${id}-title`}>
                        {title}
                    </Alert.Title>
                    <Alert.Description
                        data-testid={`${id}-description`}
                        id={`${id}-description`}
                    >
                        {message}
                    </Alert.Description>
                    <ApiErrorDetails error={error} id={`${id}-details`} />
                    {onRetry && retryLabel ? (
                        <div
                            className="mt-3"
                            data-testid={`${id}-actions`}
                            id={`${id}-actions`}
                        >
                            <Button
                                data-testid={`${id}-retry-button`}
                                id={`${id}-retry-button`}
                                onPress={onRetry}
                                variant="secondary"
                            >
                                {retryLabel}
                            </Button>
                        </div>
                    ) : null}
                </Alert.Content>
            </Alert>
        </div>
    );
};
